import { crearError, registrarError, traducirErrorSupabase } from '../../domain/errores';
import { supabase } from '../supabase/cliente';

const DEPOSITO = 'client-logos';

/** Un logotipo no necesita más; por encima, sólo ralentiza la consola. */
export const TAMANO_MAXIMO_LOGO_BYTES = 1024 * 1024;

/** Formatos que el navegador dibuja bien a cualquier tamaño. */
export const FORMATOS_LOGO = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'] as const;

/** Extensión de fichero que corresponde al tipo de imagen. */
function extension(tipo: string): string {
  const cola = tipo.split('/')[1] ?? 'png';
  // `image/svg+xml` da `svg+xml`; al fichero le basta con `svg`.
  return (cola.split('+')[0] ?? 'png').toLowerCase();
}

/**
 * Logotipos propios de cada cliente.
 *
 * La imagen va a un depósito de ficheros y la dirección resultante se guarda
 * en el cliente mediante una función del servidor, que es la que comprueba
 * quién tiene permiso para cambiarla. Desde el navegador no se toca la tabla.
 */
export class RepositorioLogos {
  /**
   * Sube el logotipo y lo asocia al cliente. Devuelve su dirección pública.
   *
   * Si se indica el anterior, se borra después de asociar el nuevo: primero
   * hay que asegurarse de que el reemplazo funcionó.
   */
  async subir(
    clienteId: number,
    fichero: File,
    direccionAnterior?: string | null,
  ): Promise<string> {
    if (fichero.size > TAMANO_MAXIMO_LOGO_BYTES) {
      throw crearError(
        'DATOS_INVALIDOS',
        { tamano: fichero.size },
        'La imagen pesa más de 1 MB. Usa una más ligera.',
      );
    }

    const tipo = fichero.type || 'image/png';
    if (!(FORMATOS_LOGO as readonly string[]).includes(tipo)) {
      throw crearError(
        'DATOS_INVALIDOS',
        { tipo },
        'Ese formato no sirve como logotipo. Usa PNG, JPG, SVG o WebP.',
      );
    }

    // El nombre lleva la marca de tiempo para que el navegador no siga
    // enseñando el logotipo anterior desde su caché.
    const ruta = `${clienteId}/logo-${Date.now()}.${extension(tipo)}`;

    const { error: errorSubida } = await supabase.storage
      .from(DEPOSITO)
      .upload(ruta, fichero, { contentType: tipo, upsert: false });

    if (errorSubida) {
      registrarError('logos.subir', errorSubida);
      throw crearError('DESCONOCIDO', errorSubida, 'No se pudo subir la imagen.');
    }

    const { data } = supabase.storage.from(DEPOSITO).getPublicUrl(ruta);
    const direccion = data?.publicUrl;

    if (!direccion) {
      throw crearError(
        'DESCONOCIDO',
        null,
        'La imagen se subió pero no se pudo obtener su enlace.',
      );
    }

    await this.asociar(clienteId, direccion);

    if (direccionAnterior && direccionAnterior !== direccion) {
      await this.borrarFichero(direccionAnterior);
    }

    return direccion;
  }

  /** Quita el logotipo del cliente; vuelve a verse el de VenIoT. */
  async quitar(clienteId: number, direccionAnterior: string | null): Promise<void> {
    await this.asociar(clienteId, null);
    if (direccionAnterior) await this.borrarFichero(direccionAnterior);
  }

  private async asociar(clienteId: number, direccion: string | null): Promise<void> {
    const { error } = await supabase.rpc('set_client_logo', {
      p_cliente_id: clienteId,
      p_logo_url: direccion,
    });

    if (error) throw traducirErrorSupabase(error);
  }

  /**
   * Borra del depósito una imagen que ya no se usa.
   *
   * Si falla no se avisa al usuario: el logotipo nuevo ya está puesto y lo
   * único que queda es un fichero suelto ocupando sitio.
   */
  async borrarFichero(direccion: string): Promise<void> {
    const coincidencia = new RegExp(`/${DEPOSITO}/(.+)$`).exec(direccion);
    const ruta = coincidencia?.[1]?.split('?')[0];
    if (!ruta) return;

    const { error } = await supabase.storage.from(DEPOSITO).remove([ruta]);
    if (error) registrarError('logos.borrarFichero', error);
  }
}

export const repositorioLogos = new RepositorioLogos();

/**
 * Isotipo del cliente: la marca reducida, para la barra lateral plegada.
 *
 * No se guarda su dirección en la base. Vive en una ruta fija dentro del mismo
 * depósito y se compone a partir del identificador del cliente, lo que evita
 * una columna nueva y una RPC nueva para un dato que siempre se pide por el
 * mismo cliente.
 *
 * **La ruta empieza por el identificador del cliente**, igual que la del
 * logotipo. No es estético: las políticas del depósito conceden el permiso
 * mirando la primera carpeta, así que cualquier otra cosa delante —`isotipos/`,
 * por ejemplo— hace que la subida se rechace con un 400.
 *
 * Va sin extensión y con el tipo declarado aparte. Así la dirección es siempre
 * la misma sea PNG, JPG o WebP, y el navegador la interpreta por el tipo que
 * anuncia el servidor, que es lo que manda.
 */
export class RepositorioIsotipos {
  private ruta(clienteId: number): string {
    return `${clienteId}/isotipo`;
  }

  /** Dirección pública. Puede no existir: quien la use debe prever el fallo. */
  direccion(clienteId: number): string {
    const { data } = supabase.storage.from(DEPOSITO).getPublicUrl(this.ruta(clienteId));

    /*
     * Se le añade la hora en curso. Sin esto, un isotipo recién subido no se
     * vería hasta vaciar la caché del navegador, porque la dirección no cambia
     * nunca.
     */
    const version = Math.floor(Date.now() / 3_600_000);
    return `${data.publicUrl}?v=${version}`;
  }

  async subir(clienteId: number, fichero: File): Promise<void> {
    if (fichero.size > TAMANO_MAXIMO_LOGO_BYTES) {
      throw crearError(
        'DATOS_INVALIDOS',
        { tamano: fichero.size },
        'La imagen pesa más de 1 MB. Usa una más ligera.',
      );
    }

    const tipo = fichero.type || 'image/png';
    if (!(FORMATOS_LOGO as readonly string[]).includes(tipo)) {
      throw crearError(
        'DATOS_INVALIDOS',
        { tipo },
        'Ese formato no sirve como isotipo. Usa PNG, JPG, SVG o WebP.',
      );
    }

    /*
     * Se borra el anterior antes de subir en vez de usar `upsert`: sobrescribir
     * exige permiso de modificación sobre el fichero, y el depósito puede tener
     * concedida sólo la creación. Si no había ninguno, el borrado no molesta.
     */
    await supabase.storage
      .from(DEPOSITO)
      .remove([this.ruta(clienteId)])
      .catch(() => undefined);

    const { error } = await supabase.storage
      .from(DEPOSITO)
      .upload(this.ruta(clienteId), fichero, { contentType: tipo, upsert: true });

    if (error) {
      registrarError('isotipos.subir', error);
      throw crearError('DESCONOCIDO', error, 'No se pudo subir el isotipo.');
    }
  }

  async quitar(clienteId: number): Promise<void> {
    const { error } = await supabase.storage.from(DEPOSITO).remove([this.ruta(clienteId)]);
    if (error) registrarError('isotipos.quitar', error);
  }
}

export const repositorioIsotipos = new RepositorioIsotipos();
